import { useState, useEffect } from 'react';
import { Loader2, Clock, Calendar, Mic, FileText, Globe, Stethoscope, Euro, Users, Download, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { BeheerGebruiker, VerbruikRegel } from '@/lib/api-types';

/**
 * Een verbruiksregel zoals dit scherm hem gebruikt. VerbruikRegel uit api-types is de
 * volledige vorm; hier staan de velden die het overzicht nodig heeft.
 */
type UsageLog = VerbruikRegel;
type PracticeUser = BeheerGebruiker;

const USAGE_TYPE_LABELS: Record<string, { label: string; icon: typeof Mic }> = {
  speech_recording: { label: 'Spraakopname', icon: Mic },
  soep_transcription: { label: 'SOEP-transcriptie', icon: FileText },
  translation: { label: 'Vertaling', icon: Globe },
  icpc_suggestion: { label: 'ICPC-suggestie', icon: Stethoscope },
  patient_summary: { label: 'Patiëntsamenvatting (PDF)', icon: FileText },
  acs_call: { label: 'Telefoongesprek (ACS)', icon: Phone },
};

function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.round(totalSeconds % 60);
  if (hours > 0) return `${hours}u ${minutes}m ${secs}s`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

function formatMonth(monthStr: string): string {
  const [year, month] = monthStr.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
}

export function AiCostsTab({ practiceId, practiceName, agbCode, afasId }: { practiceId?: string; practiceName?: string; agbCode?: string; afasId?: string | null }) {
  const [logs, setLogs] = useState<UsageLog[]>([]);
  const [users, setUsers] = useState<PracticeUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!practiceId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    void (async () => {
      try {
        // Twee aanroepen in plaats van één antwoord met alles erin. get-practice-usage
        // gaf logs, gebruikers, praktijken en maandtotalen tegelijk terug, ongeacht
        // wat de aanroeper nodig had -- en zonder enige autorisatiecontrole.
        const [verbruik, gebruikers] = await Promise.all([
          api.get<{ entries: VerbruikRegel[] }>('/admin/usage/entries', {
            query: { practiceId, limit: 2000 },
          }),
          api.get<{ users: BeheerGebruiker[] }>('/admin/practice-users', {
            query: { practiceId },
          }),
        ]);
        setLogs(verbruik.entries);
        setUsers(gebruikers.users);
      } catch {
        // Een leeg overzicht is hier beter dan een foutmelding: dit tabblad staat in
        // een dialoog naast de praktijkinstellingen, die wel bruikbaar blijven.
        setLogs([]);
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [practiceId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!practiceId) {
    return <p className="text-sm text-muted-foreground italic">Geen praktijk geselecteerd.</p>;
  }

  const totalSeconds = logs.reduce((sum, l) => sum + Number(l.seconds), 0);
  const totalCost = logs.reduce((sum, l) => sum + (l.sellingCost ?? 0), 0);
  const totalPurchase = logs.reduce((sum, l) => sum + (l.purchaseCost ?? 0), 0);

  // Per-type breakdown
  const typeData = logs.reduce((acc, log) => {
    const type = log.usageType || 'speech_recording';
    if (!acc[type]) acc[type] = { count: 0, seconds: 0, cost: 0, purchase: 0 };
    acc[type].count += 1;
    if (type === 'speech_recording' || type === 'acs_call') acc[type].seconds += Number(log.seconds);
    acc[type].cost += log.sellingCost ?? 0;
    acc[type].purchase += log.purchaseCost ?? 0;
    return acc;
  }, {} as Record<string, { count: number; seconds: number; cost: number; purchase: number }>);

  // Per-user breakdown
  const userData = logs.reduce((acc, log) => {
    const uid = log.practiceUserId || 'unknown';
    if (!acc[uid]) acc[uid] = { count: 0, seconds: 0, cost: 0, purchase: 0 };
    acc[uid].count += 1;
    acc[uid].seconds += Number(log.seconds);
    acc[uid].cost += log.sellingCost ?? 0;
    acc[uid].purchase += log.purchaseCost ?? 0;
    return acc;
  }, {} as Record<string, { count: number; seconds: number; cost: number; purchase: number }>);

  const userMap = Object.fromEntries(users.map(u => [u.id, u]));

  // Monthly costs
  const monthlyCosts = logs.reduce((acc, log) => {
    const month = new Date(log.createdAt).toISOString().slice(0, 7);
    if (!acc[month]) acc[month] = { seconds: 0, cost: 0, count: 0, purchase: 0 };
    acc[month].seconds += Number(log.seconds);
    acc[month].cost += log.sellingCost ?? 0;
    acc[month].purchase += log.purchaseCost ?? 0;
    acc[month].count += 1;
    return acc;
  }, {} as Record<string, { seconds: number; cost: number; count: number; purchase: number }>);

  const sortedMonths = Object.entries(monthlyCosts).sort(([a], [b]) => b.localeCompare(a));

  const handleExportExcel = () => {
    if (sortedMonths.length === 0) return;
    const sep = ';';
    const header = ['AGBCode', 'Praktijknaam', 'AFAS ID', 'InkoopExBtw', 'VerkoopExBTW', 'MaandPeriode', 'JaarPeriode'].join(sep);
    const rows = sortedMonths.map(([month, data]) => {
      const [year, m] = month.split('-');
      return [
        agbCode || '',
        practiceName || '',
        afasId || '',
        data.purchase.toFixed(4).replace('.', ','),
        data.cost.toFixed(4).replace('.', ','),
        m,
        year,
      ].join(sep);
    });
    const bom = '\uFEFF';
    const csv = bom + [header, ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kosten-omzet-${(practiceName || 'praktijk').replace(/\s+/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Export button */}
      {sortedMonths.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <Download className="h-4 w-4 mr-1.5" />
            Exporteer naar Excel
          </Button>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Totaal AI-gebruik</span>
          </div>
          <p className="text-2xl font-bold">{formatDuration(totalSeconds)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {logs.length} AI-acties totaal
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Euro className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Inkoopkosten</span>
          </div>
          <p className="text-2xl font-bold">€{totalPurchase.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">totale inkoop</p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Euro className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Verkoopomzet</span>
          </div>
          <p className="text-2xl font-bold">€{totalCost.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">marge €{(totalCost - totalPurchase).toFixed(2)}</p>
        </div>
      </div>

      {/* Per-type breakdown */}
      <div>
        <span className="text-sm font-medium">Per type</span>
        <div className="mt-2 space-y-1">
          {Object.entries(typeData).map(([type, data]) => {
            const config = USAGE_TYPE_LABELS[type] || { label: type, icon: Clock };
            const Icon = config.icon;
            return (
              <div key={type} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                <div className="flex items-center gap-2">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm">{config.label}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium">
                    {type === 'speech_recording' || type === 'acs_call' ? formatDuration(data.seconds) : `${data.count}×`}
                  </span>
                  <p className="text-[10px] text-muted-foreground">inkoop €{data.purchase.toFixed(4)} · verkoop €{data.cost.toFixed(4)}</p>
                </div>
              </div>
            );
          })}
          {Object.keys(typeData).length === 0 && (
            <p className="text-sm text-muted-foreground italic">Nog geen AI-gebruik geregistreerd.</p>
          )}
        </div>
      </div>

      {/* Per-user breakdown */}
      {Object.keys(userData).length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Per gebruiker</span>
          </div>
          <div className="space-y-1">
            {Object.entries(userData).map(([uid, data]) => {
              const user = userMap[uid];
              const name = user?.displayName || user?.externalUserId || uid;
              return (
                <div key={uid} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                  <span className="text-sm">{name}</span>
                  <div className="text-right">
                    <span className="text-sm font-medium">{data.count} acties</span>
                    <p className="text-[10px] text-muted-foreground">inkoop €{data.purchase.toFixed(2)} · verkoop €{data.cost.toFixed(2)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Monthly breakdown */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Per maand</span>
        </div>
        
        {sortedMonths.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            Nog geen gebruik geregistreerd.
          </p>
        ) : (
          <div className="space-y-1">
            {sortedMonths.map(([month, data]) => (
              <div key={month} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                <span className="text-sm capitalize">{formatMonth(month)}</span>
                <div className="text-right">
                  <span className="text-sm font-medium">{data.count} acties</span>
                  <p className="text-[10px] text-muted-foreground">inkoop €{data.purchase.toFixed(2)} · verkoop €{data.cost.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
